import { useUserStore } from '@/stores/user.js'
import { viteConfig } from './utils.js'

// API Configuration
const API_BASE_URL = viteConfig('API_BASE_URL')

// HTTP Client utility
class ApiClient {
  constructor(baseURL = API_BASE_URL) {
    this.baseURL = baseURL
  }

  async request(endpoint, options = {}) {
    const userStore = useUserStore()
    const url = `${this.baseURL}${endpoint}`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Accept-Language': userStore.currentLanguage,
        ...options.headers,
      },
      ...options,
    }

    // Add auth token if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        let errorData
        try {
          errorData = await response.json()
        } catch {
          errorData = { message: `HTTP error! status: ${response.status}` }
        }
        
        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`)
        error.response = {
          status: response.status,
          data: errorData
        }
        throw error
      }
      
      const data = await response.json()
      
      // Process userState if present in response
      if (data.userState) {
        // Import Game class dynamically to avoid circular imports
        const { Game } = await import('./game.js')
        Game.processUserState(data.userState)
      }
      
      return data
    } catch (error) {
      console.error('API Request failed:', error)
      throw error
    }
  }

  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${endpoint}?${queryString}` : endpoint
    
    return this.request(url, {
      method: 'GET',
    })
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    })
  }
}

// Create API client instance
export const apiClient = new ApiClient()

// Events API service
export const eventsAPI = {
  // Get all events with optional filters
  async getEvents(params = {}) {
    return apiClient.get('/events', params)
  },

  // Get specific event by ID
  async getEvent(id) {
    return apiClient.get(`/events/${id}`)
  },

  // Get upcoming events (next 24 hours)
  async getUpcomingEvents(params = {}) {
    return apiClient.get('/events/upcoming', params)
  },

  // Get events by sport
  async getEventsBySport(sportKey, params = {}) {
    return apiClient.get(`/events/sport/${sportKey}`, params)
  },

  // Get available sports
  async getSports() {
    return apiClient.get('/events/sports')
  },
}

// Authentication API service
export const authAPI = {
  // User login
  async login(credentials) {
    const response = await apiClient.post('/login', credentials)
    if (response.success && response.data.access_token) {
      // Store only auth token
      localStorage.setItem('auth_token', response.data.access_token)
    }
    return response
  },

  // User registration
  async register(userData) {
    const response = await apiClient.post('/register', userData)
    if (response.success && response.data.access_token) {
      // Store only auth token
      localStorage.setItem('auth_token', response.data.access_token)
    }
    return response
  },

  // User logout
  async logout() {
    try {
      await apiClient.post('/logout')
    } catch (error) {
      console.error('Logout API call failed:', error)
    } finally {
      // Clear auth token
      localStorage.removeItem('auth_token')
    }
  },

  // Get current user info
  async getCurrentUser() {
    return apiClient.get('/user')
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('auth_token')
  },

  // Clear authentication data
  clearAuth() {
    localStorage.removeItem('auth_token')
  }
  ,

  // Request password reset (send reset link to email)
  async requestPasswordReset(email) {
    return apiClient.post('/password/email', { email })
  }
  ,

  // Perform password reset with token
  async resetPassword(payload) {
    return apiClient.post('/password/reset', payload)
  }
}

// User Bets API service
export const userBetsAPI = {
  // Get user's bets
  async getMyBets(params = {}) {
    return apiClient.get('/user/bets', params)
  },

  // Place a new bet
  async placeBet(betData) {
    return apiClient.post('/user/bets', betData)
  },
  async placeCombo(betData) {
    return apiClient.post('/user/bets/combo', betData)
  },

  // Get specific bet by ID
  async getBet(id) {
    return apiClient.get(`/user/bets/${id}`)
  },

  // Get user's betting statistics
  async getBettingStats() {
    return apiClient.get('/user/bets/stats')
  }
}


// Dashboard API service
export const dashboardAPI = {
  // Get dashboard statistics
  async getStats() {
    return apiClient.get('/dashboard/stats')
  },

  // Get standings by type (overall, month, week)
  async getStandings(type, timeframe, page = 1, perPage = 25) {
    return apiClient.get(`/dashboard/standings/${type}/${timeframe}`, {
      page, 
      per_page: perPage
    })
  }
}

// User Settings API service
export const userSettingsAPI = {
  // Get user settings
  async getSettings() {
    return apiClient.get('/user/settings')
  },

  // Update user settings
  async updateSettings(settings) {
    return apiClient.put('/user/settings', settings)
  }
}

// Export default API client
export default apiClient